import { atom } from 'jotai'
import dayjs from 'dayjs'
import { currentSpaceAtom } from '@/store/auth.atom'
import { currentTierAtom, TrialStatus } from '@/store/pricing.atom'

/**
 * Trial Atoms (RFC-001)
 *
 * State management for trial status, expiration, and lifecycle
 */

// ============================================
// Trial Configuration
// ============================================

export const TRIAL_CONFIG = {
  durationDays: 14,
  expiringThresholdDays: 3,
  reminderDays: [7, 3, 1],
  sandboxRetentionDays: 90,
} as const

// ============================================
// Atoms
// ============================================

/**
 * Current trial status
 */
export const trialStatusAtom = atom<TrialStatus>((get) => {
  const space = get(currentSpaceAtom)
  if (!space) return 'NONE'

  // IMPORTANT: Check pricing_tier FIRST as it's the definitive source of truth
  // If pricing_tier is set and NOT 'TRIAL', user has converted (STARTER/PREMIUM/ENTERPRISE/SANDBOX)
  // This takes precedence over trial_status which may not be updated immediately after Stripe checkout
  if (space.pricing_tier && space.pricing_tier !== 'TRIAL') {
    if (space.pricing_tier === 'SANDBOX') return 'EXPIRED'
    return 'CONVERTED'
  }

  // Check for explicit trial_status field (only relevant when pricing_tier is TRIAL or not set)
  if (space.trial_status) {
    return space.trial_status
  }

  // Check for active trial via trial_expires_at (only for TRIAL tier or legacy accounts)
  if (space.trial_expires_at) {
    const expiresAt = new Date(space.trial_expires_at)
    const now = new Date()
    const daysRemaining = Math.ceil(
      (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    )

    if (daysRemaining <= 0) return 'EXPIRED'
    if (daysRemaining <= TRIAL_CONFIG.expiringThresholdDays) return 'EXPIRING'
    return 'ACTIVE'
  }

  // If trial_started_at is set, it's a new trial (trial_expires_at might not be set yet)
  if (space.trial_started_at) {
    return 'ACTIVE'
  }

  // Fallback: determine from tier
  const tier = get(currentTierAtom)
  if (tier === 'TRIAL') return 'ACTIVE'
  if (tier === 'SANDBOX') return 'EXPIRED'
  return 'CONVERTED'
})

/**
 * Whether the space is currently in trial
 */
export const isTrialAtom = atom<boolean>((get) => {
  const status = get(trialStatusAtom)
  return status === 'ACTIVE' || status === 'EXPIRING'
})

/**
 * Trial expiration date
 */
export const trialExpiresAtAtom = atom<Date | null>((get) => {
  const space = get(currentSpaceAtom)
  if (!space?.trial_expires_at) return null
  return new Date(space.trial_expires_at)
})

/**
 * Days remaining in trial
 */
export const trialDaysRemainingAtom = atom<number | null>((get) => {
  const expiresAt = get(trialExpiresAtAtom)
  if (!expiresAt) {
    // Fallback: if in trial but no expires date, assume full trial
    const isTrial = get(isTrialAtom)
    return isTrial ? TRIAL_CONFIG.durationDays : null
  }

  const now = dayjs()
  const expires = dayjs(expiresAt)
  const daysRemaining = expires.diff(now, 'day')
  return Math.max(0, daysRemaining)
})

/**
 * Whether trial is expiring soon (within 3 days)
 */
export const isTrialExpiringSoonAtom = atom<boolean>((get) => {
  const daysRemaining = get(trialDaysRemainingAtom)
  if (daysRemaining === null) return false
  return daysRemaining <= TRIAL_CONFIG.expiringThresholdDays
})

/**
 * Whether trial is on last day
 */
export const isTrialLastDayAtom = atom<boolean>((get) => {
  const daysRemaining = get(trialDaysRemainingAtom)
  if (daysRemaining === null) return false
  return daysRemaining <= 1
})

/**
 * Trial progress percentage (0-100)
 */
export const trialProgressAtom = atom<number>((get) => {
  const daysRemaining = get(trialDaysRemainingAtom)
  if (daysRemaining === null) return 0
  const daysUsed = TRIAL_CONFIG.durationDays - daysRemaining
  return Math.min(
    100,
    Math.max(0, (daysUsed / TRIAL_CONFIG.durationDays) * 100)
  )
})

/**
 * Trial started date
 */
export const trialStartedAtAtom = atom<Date | null>((get) => {
  const space = get(currentSpaceAtom)
  if (!space?.trial_started_at) return null
  return new Date(space.trial_started_at)
})

/**
 * Whether trial has been converted to paid
 */
export const isTrialConvertedAtom = atom<boolean>((get) => {
  const status = get(trialStatusAtom)
  return status === 'CONVERTED'
})

/**
 * Whether trial has expired (now in sandbox)
 */
export const isTrialExpiredAtom = atom<boolean>((get) => {
  const status = get(trialStatusAtom)
  return status === 'EXPIRED' || status === 'CHURNED'
})

/**
 * Formatted trial expiry date string
 */
export const trialExpiryDateStringAtom = atom<string | null>((get) => {
  const expiresAt = get(trialExpiresAtAtom)
  if (!expiresAt) return null
  return dayjs(expiresAt).format('MMMM D, YYYY')
})

/**
 * Trial urgency level for UI styling
 */
export const trialUrgencyAtom = atom<'none' | 'info' | 'warning' | 'urgent'>(
  (get) => {
    const isTrial = get(isTrialAtom)
    if (!isTrial) return 'none'

    const daysRemaining = get(trialDaysRemainingAtom)
    if (daysRemaining === null) return 'info'
    if (daysRemaining <= 1) return 'urgent'
    if (daysRemaining <= 3) return 'warning'
    return 'info'
  }
)

/**
 * Get trial banner message
 */
export const trialBannerMessageAtom = atom<string | null>((get) => {
  const isTrial = get(isTrialAtom)
  const isExpired = get(isTrialExpiredAtom)
  const daysRemaining = get(trialDaysRemainingAtom)
  const expiryDate = get(trialExpiryDateStringAtom)

  if (isExpired) {
    return 'Your trial has ended. Upgrade to continue using all features.'
  }

  if (!isTrial) return null

  if (daysRemaining === 0 || daysRemaining === 1) {
    return 'Your trial ends today! Upgrade now to keep all your data and features.'
  }

  if (daysRemaining !== null && daysRemaining <= 3) {
    return `Only ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left in your trial. Upgrade to continue.`
  }

  if (daysRemaining !== null && daysRemaining <= 7) {
    return `${daysRemaining} days remaining in your Premium trial${expiryDate ? ` (expires ${expiryDate})` : ''}`
  }

  return `You're on a 14-day Premium trial${expiryDate ? ` until ${expiryDate}` : ''}`
})

/**
 * Features available during trial
 */
export const trialFeaturesAtom = atom<string[]>(() => [
  'Desk booking',
  'Room booking',
  'Visitor management',
  'Calendar sync (Premium)',
  'SSO integration (Premium)',
  'Advanced analytics (Premium)',
])

/**
 * Should show trial banner
 */
export const shouldShowTrialBannerAtom = atom<boolean>((get) => {
  const space = get(currentSpaceAtom)
  const isTrial = get(isTrialAtom)
  const isExpired = get(isTrialExpiredAtom)
  const tier = get(currentTierAtom)

  // Don't show banner for legacy accounts without PLG trial fields
  // Only show if the account has explicit trial/pricing setup
  if (
    space &&
    !space.pricing_tier &&
    !space.trial_status &&
    !space.trial_started_at &&
    !space.trial_expires_at
  ) {
    // Legacy account without PLG fields - don't show trial banner
    return false
  }

  // Show for trial or sandbox modes
  return isTrial || isExpired || tier === 'SANDBOX'
})

/**
 * Sandbox data retention days remaining
 */
export const sandboxDaysRemainingAtom = atom<number | null>((get) => {
  const tier = get(currentTierAtom)
  if (tier !== 'SANDBOX') return null

  const expiresAt = get(trialExpiresAtAtom)
  if (!expiresAt) return TRIAL_CONFIG.sandboxRetentionDays

  const sandboxStartDate = dayjs(expiresAt) // Sandbox starts when trial expires
  const retentionEnds = sandboxStartDate.add(
    TRIAL_CONFIG.sandboxRetentionDays,
    'day'
  )
  const daysRemaining = retentionEnds.diff(dayjs(), 'day')

  return Math.max(0, daysRemaining)
})
